'use client';

import {motion} from 'motion/react';
import * as React from 'react';
import {useState} from 'react';

import styles from './flip-card.module.scss';

const TRANSITION_CONFIG = {
    duration: 0.7,
    ease: [0.4, 0.2, 0.2, 1],
    transition: '0.7s cubic-bezier(0.4, 0.2, 0.2, 1)',
} as const;

interface FlipCardSideProps {
    title: string;
    titleColor: string;
    number: string | null;
    backgroundImage: string;
    description: React.ReactNode;
    status?: string;
    isFlipped: boolean;
    toggleFlip: () => void;
}

interface FlipCardProps {
    frontTitle: string;
    frontTitleColor?: string;
    backTitle: string;
    backTitleColor?: string;
    number: string | null;
    backgroundImage: string;
    description: React.ReactNode;
    status?: string;
    onFlip?: (flipped: boolean) => void;
}

interface FlipButtonProps {
    onClick: () => void;
    isPlus?: boolean;
    className?: string;
}

interface CardTitleProps {
    title: string;
    titleColor: string;
    number?: string | null;
    isBackSide?: boolean;
    className?: string;
}

const FlipButton = ({onClick, isPlus = true, className}: FlipButtonProps) => {
    return (
        <div
            className={`${className} ${styles.flipButton}`}
            role="button"
            aria-label={isPlus ? 'Flip card' : 'Flip card back'}
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onClick();
                }
            }}
        >
            <div className={isPlus ? styles.plusIcon : styles.closeIcon}>{isPlus ? '+' : '×'}</div>
        </div>
    );
};

const CardTitle = ({title, titleColor, number, isBackSide = false, className}: CardTitleProps) => {
    const titleStyles = isBackSide ? styles.cardTitleFront : styles.cardTitle;
    const titleTextStyles = isBackSide ? styles.titleText : styles.titleTextFront;
    const dividerStyles = isBackSide ? styles.divider : styles.dividerFront;
    const numberStyles = isBackSide ? styles.numberText : styles.numberTextFront;

    return (
        <header className={`${titleStyles} ${className}`}>
            <h2 className={titleTextStyles} style={{color: titleColor}}>
                {title}
            </h2>
            <div className={styles.dividerContainer}>
                <div className={dividerStyles} style={{borderColor: titleColor}} />
                {!number && <div className={styles.dot} style={{backgroundColor: titleColor}} />}
            </div>
            {number && (
                <span className={numberStyles} style={{color: titleColor}}>
                    {number}
                </span>
            )}
        </header>
    );
};

const CardFront = ({
    title,
    titleColor,
    number,
    backgroundImage,
    toggleFlip,
    isFlipped,
}: FlipCardSideProps) => {
    return (
        <motion.section
            initial={false}
            className={`${styles.side} ${styles.front}`}
            animate={{
                rotateY: isFlipped ? 180 : 0,
                zIndex: isFlipped ? 0 : 1,
            }}
            transition={TRANSITION_CONFIG}
            style={{
                backgroundImage: `url(${backgroundImage})`,
                backfaceVisibility: 'hidden',
            }}
        >
            <div className={styles.topRow}>
                <CardTitle
                    titleColor={titleColor}
                    className={styles.titleTextFront}
                    title={title}
                    number={number}
                />
            </div>
            <div className={styles.filler} />
            <div className={styles.bottomRow}>
                <FlipButton onClick={toggleFlip} isPlus={true} />
            </div>
        </motion.section>
    );
};

const CardBack = ({
    title,
    titleColor,
    number,
    description,
    toggleFlip,
    isFlipped,
    status,
}: FlipCardSideProps) => {
    return (
        <motion.section
            className={`${styles.side} ${styles.back}`}
            transition={TRANSITION_CONFIG}
            initial={false}
            animate={{
                rotateY: isFlipped ? 0 : -180,
                zIndex: isFlipped ? 1 : 0,
            }}
            style={{
                backfaceVisibility: 'hidden',
            }}
        >
            <CardTitle titleColor={titleColor} title={title} number={number} isBackSide={true} />
            <div className={styles.filler} />
            <div className={styles.descriptionContainer}>
                <div className={styles.description}>{description}</div>
                <div className={styles.status}>{status}</div>
            </div>
            <div className={styles.bottomRow}>
                <FlipButton onClick={toggleFlip} className={styles.flipClose} isPlus={false} />
            </div>
        </motion.section>
    );
};

function FlipCard({
    frontTitle,
    frontTitleColor = '#fff',
    backTitle,
    backTitleColor = '#000',
    number,
    backgroundImage,
    description,
    status,
    onFlip,
}: FlipCardProps) {
    const [isFlipped, setIsFlipped] = useState(false);

    function toggleFlip() {
        setIsFlipped(!isFlipped);
        onFlip?.(!isFlipped);
    }

    return (
        <article className={styles.featureCard}>
            <div className={styles.cardContainer}>
                <CardFront
                    isFlipped={isFlipped}
                    title={frontTitle}
                    titleColor={frontTitleColor}
                    number={number}
                    backgroundImage={backgroundImage}
                    description={description}
                    status={status}
                    toggleFlip={toggleFlip}
                />
                <CardBack
                    isFlipped={isFlipped}
                    title={backTitle}
                    titleColor={backTitleColor}
                    number={number}
                    backgroundImage={backgroundImage}
                    description={description}
                    status={status}
                    toggleFlip={toggleFlip}
                />
            </div>
        </article>
    );
}

export {FlipCard};
